import React from 'react'

import { useWorkflowProgress } from '@hooks/use-workflow-progress.hook'
import { Icon } from '@components/icon.component'

type WorkflowProgressBarProps = {
  activity: any
  activityUser: any
  href?: string
  size?: string
}

export function WorkflowProgressBar({
  activity,
  activityUser,
  href = '',
  size = '',
}: WorkflowProgressBarProps) {
  const { startDate, endDate, progressStatus, dateProgress } =
    useWorkflowProgress(activity, activityUser.completedPercentage)
  const isSmall = size === 'sm'

  function normalizeProgress() {
    const { completedPercentage } = activityUser
    if (completedPercentage > 100) {
      return 100
    } else if (completedPercentage < 0 || !completedPercentage) {
      return 0
    }
    return completedPercentage
  }

  const progress = normalizeProgress()
  const progressBar = isSmall ? (
    <>
      <div className="w-full relative flex flex-col">
        <div
          className="h-[0.5rem] w-full overflow-hidden rounded-full bg-grey-200"
          role="progressbar"
          aria-valuetext={'time left'}
          aria-label={`activity ${activityUser.completedPercentage} percent complete`}
          aria-valuenow={progress}
        >
          <div
            className="h-full bg-primary data-[status='*']:bg-transparent data-[status=completed]:bg-success data-[status=overdue]:bg-warning"
            data-status={progressStatus}
            style={{ width: `${normalizeProgress()}%` }}
          />
        </div>
      </div>
    </>
  ) : (
    <>
      <div className="shrink-0 text-[1.2rem] leading-[1.5rem] font-semibold">
        <span className="font-normal">{startDate.toFormat('LLL')} </span>
        <span>{startDate.toFormat('d')}</span>
      </div>
      <div className="relative mx-[1.2rem] w-full flex flex-col">
        <div
          className="absolute -top-[1.6rem] -translate-x-1/2 text-center text-[1rem] text-grey-600"
          style={{ left: `${dateProgress}%` }}
        >
          <div className="whitespace-nowrap">Today</div>
          <span className="absolute left-1/2 top-full h-[0.8rem] w-[0.3rem] -translate-x-1/2 rounded-[0.2rem] bg-grey-800" />
        </div>
        <div
          className="h-[0.5rem] w-full overflow-hidden rounded-full bg-grey-200"
          role="progressbar"
          aria-valuetext={'time left'}
          aria-label={`activity ${activityUser.completedPercentage} percent complete`}
          aria-valuenow={progress}
        >
          <div
            className="h-full bg-primary data-[status='*']:bg-transparent data-[status=completed]:bg-success data-[status=overdue]:bg-warning"
            data-status={progressStatus}
            style={{ width: `${normalizeProgress()}%` }}
          />
        </div>
      </div>
      <div className="shrink-0 text-[1.2rem] leading-[1.5rem] font-semibold">
        <span className="font-normal">{endDate.toFormat('LLL')} </span>
        <span>{endDate.toFormat('d')}</span>
      </div>
      {href && (
        <div className="ml-2 border-l-2 border-grey-300 px-1 text-grey-500">
          <Icon name="angle-right" />
        </div>
      )}
    </>
  )

  return (
    <div
      className={`flex w-full items-center rounded-full bg-grey-25 border-none transition-colors duration-200 ease-in-out ${isSmall ? 'min-w-0 p-[0.8rem]' : 'min-w-[260px] p-[1.2rem]'} ${href ? 'hover:bg-grey-75' : ''}`}
    >
      {progressBar}
    </div>
  )
}
